import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import InstallPWA from './components/InstallPWA';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import RegistrationSuccessPage from './pages/RegistrationSuccessPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SubscriptionPage from './pages/SubscriptionPage';
import DashboardPage from './pages/DashboardPage';
import AlertsPage from './pages/AlertsPage';
import AlertFormPage from './pages/AlertFormPage';
import JobMatchesPage from './pages/JobMatchesPage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from './pages/SettingsPage';

// IMG Guide Pages
import IMGGuidePage from './pages/IMGGuidePage';
import ActGuidePage from './pages/ActGuidePage';
import TrustGradeGuidePage from './pages/TrustGradeGuidePage';
import LEDGuidePage from './pages/LEDGuidePage';
import PLABGuidePage from './pages/PLABGuidePage';
import ReferralRedirect from './pages/ReferralRedirect';
import AffiliateDashboard from './pages/AffiliateDashboard';

// Admin Pages
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminDiscountCodes from './pages/AdminDiscountCodes';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AdminScraper from './pages/AdminScraper';
import AdminSupport from './pages/AdminSupport';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/registration-success" element={<RegistrationSuccessPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            
            {/* IMG Guide Routes */}
            <Route path="/img-guide" element={<IMGGuidePage />} />
            <Route path="/img-guide/medical-training-prioritisation-act-2026" element={<ActGuidePage />} />
            <Route path="/img-guide/trust-grade-jobs" element={<TrustGradeGuidePage />} />
            <Route path="/img-guide/led-jobs" element={<LEDGuidePage />} />
            <Route path="/img-guide/plab-jobs" element={<PLABGuidePage />} />
            
            {/* Referral Routes */}
            <Route path="/r/:slug" element={<ReferralRedirect />} />
            <Route path="/affiliate/:slug" element={<AffiliateDashboard />} />
            
            {/* Protected Routes (require auth) */}
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <SubscriptionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/success"
              element={
                <ProtectedRoute>
                  <SubscriptionPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Routes (require auth + subscription) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireSubscription>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts"
              element={
                <ProtectedRoute requireSubscription>
                  <AlertsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts/new"
              element={
                <ProtectedRoute requireSubscription>
                  <AlertFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts/:id/edit"
              element={
                <ProtectedRoute requireSubscription>
                  <AlertFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs"
              element={
                <ProtectedRoute requireSubscription>
                  <JobMatchesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute requireSubscription>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute requireSubscription>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/discount-codes"
              element={
                <ProtectedRoute>
                  <AdminDiscountCodes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subscriptions"
              element={
                <ProtectedRoute>
                  <AdminSubscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/scraper"
              element={
                <ProtectedRoute>
                  <AdminScraper />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/support"
              element={
                <ProtectedRoute>
                  <AdminSupport />
                </ProtectedRoute>
              }
            />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
        <InstallPWA />
      </div>
    </AuthProvider>
  );
}

export default App;
